import { getMessages } from "./chatApi";
import type { DmConversation, ZulipMessage, ZulipMessageRecipient } from "./types";

/**
 * Sorted, comma-joined participant-id key — the stable identity of a
 * DM/group-DM thread. Always built from the *other* participants only
 * (self excluded), matching Zulip's own `to`/`dm` narrow operand convention
 * (a 1:1 DM's operand is `[other_user_id]`, never `[my_id, other_user_id]`)
 * — so the same key can be computed from a Selection (which only ever holds
 * the other side) or from a fetched message's display_recipient (which
 * includes self) as long as self is filtered out first in the latter case.
 * A self-DM (notes to self) is the one case with no "other" participant —
 * callers fall back to `[myUserId]` for that key.
 */
export function dmKeyFromUserIds(userIds: number[]): string {
  return [...new Set(userIds)].sort((a, b) => a - b).join(",");
}

function participantsOf(message: ZulipMessage): ZulipMessageRecipient[] {
  return Array.isArray(message.display_recipient) ? message.display_recipient : [];
}

/** Builds the DmConversation this message belongs to, or null if it isn't a DM. */
export function dmConversationFromMessage(message: ZulipMessage, myUserId: number): DmConversation | null {
  const participants = participantsOf(message);
  if (!participants.length) return null;
  const others = participants.filter((p) => p.id !== myUserId);
  const userIds = others.length ? others.map((p) => p.id) : [myUserId];
  return {
    key: dmKeyFromUserIds(userIds),
    userIds,
    label: others.length ? others.map((p) => p.full_name).join(", ") : "Notes to self",
    lastMessage: message,
    lastTimestamp: message.timestamp,
  };
}

/**
 * No dedicated "DM conversation list" exists in Zulip's API — this fetches a
 * bounded window of recent DMs and groups them client-side by the
 * other-participant-id key (see dmKeyFromUserIds).
 */
export async function loadDmConversations(myUserId: number): Promise<DmConversation[]> {
  const { messages } = await getMessages({
    narrow: [{ operator: "is", operand: "dm" }],
    anchor: "newest",
    numBefore: 200,
    numAfter: 0,
  });

  const byKey = new Map<string, DmConversation>();
  for (const message of messages) {
    const conversation = dmConversationFromMessage(message, myUserId);
    if (!conversation) continue;
    const existing = byKey.get(conversation.key);
    if (!existing || conversation.lastTimestamp > existing.lastTimestamp) {
      byKey.set(conversation.key, conversation);
    }
  }

  return [...byKey.values()].sort((a, b) => b.lastTimestamp - a.lastTimestamp);
}

/** Inserts/updates one conversation (by its key) and keeps the list sorted by recency. */
export function upsertDmConversation(list: DmConversation[], incoming: DmConversation): DmConversation[] {
  const idx = list.findIndex((c) => c.key === incoming.key);
  const next = idx === -1 ? [incoming, ...list] : list.map((c, i) => (i === idx ? incoming : c));
  return next.sort((a, b) => b.lastTimestamp - a.lastTimestamp);
}
